/* ------------------------ Functional imports start ------------------------ */
import { getTimeString, parseDisableLoggingEnvVar } from './utils'
/* ------------------------- Functional imports end ------------------------- */

// this object just helps doing gte and lte comparisons between logLevels
const severity = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
}

export default class LogTool {
  context: string
  logLevel: 'debug' | 'info' | 'warn' | 'error'
  enable: boolean

  /**
   * Create a new LogTool instance to write log-messages from a react component.
   * @param  config Log-Instance configuration object.
   * @param  config.context: Semantic context of the logger; e.g. the components name.
   * @param  config.enable: flag that enables/disables the LogTool-instance.
   * @param  config.logLevel: The lowest logging level that should be logged.
   */
  constructor({
    context,
    enable = true,
    logLevel = 'debug',
  }: {
    context: string
    enable?: boolean
    logLevel?: 'debug' | 'info' | 'warn' | 'error'
  }) {
    this.context = context
    this.logLevel = logLevel
    // Handle environments where process is not defined (e.g., Cypress component tests)
    const disableLogging = typeof process.env !== 'undefined' ? process.env.REACT_APP_DISABLE_LOGGING : undefined
    this.enable = !parseDisableLoggingEnvVar(disableLogging) && enable
  }

  /**
   * Get the name of the function that called the log method.
   * @param callerDistance the number of stack trace entries the searched caller functions should be above the function calling getCallerName.
   * @returns name of function as string
   */
  getCallerName(callerDistance: number): string {
    // disable this function in production as it could cause security risks
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'production') return ''

    try {
      throw new Error()
    } catch (e: any) {
      // try to find out which browser is being used
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      if (e.stack.match(/(\w+)@(\w+):/g)) {
        // browser: Firefox
        // console.log("Browser: Firefox")
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const allMatches = e.stack.match(/(\w+)@(\w+):/g)
        // console.log("e ->", e, "stack ->", e.stack, "allMatches ->", allMatches);

        // in rare occasions allMatches[callerDistance] can be undefined
        // therefore use allMatches[callerDistance]?.match...
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const callerMatches = allMatches[callerDistance]?.match(/(\w+)@\w+:/)
        // since allMatches[callerDistance] may be undefined, callerMatches may be undefined as well
        if (!callerMatches) return 'caller_not_found'
        // console.log("callerMatches ->", callerMatches)
        return callerMatches[1]
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      if (e.stack.match(/at ((async )?(\w+(\.\w+)*)( \(|\.tsx)|http)/g)) {
        // browser: Chrome, Edge, Opera, ... (Chromium)
        /* console.log("Browser: Chromium based") */
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const allMatches = e.stack.match(/at ((async )?(\w+(\.\w+)*)( \(|\.tsx)|http)/g)
        // console.log("e ->", e, "stack ->", e.stack, "allMatches ->", allMatches);

        // in rare occasions allMatches[callerDistance] can be undefined
        // therefore use allMatches[callerDistance]?.match...
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        const callerMatches = allMatches[callerDistance]?.match(
          /at ((async )?(\w+(\.\w+)*)( \(|\.tsx)|http)/
        )
        // since allMatches[callerDistance] may be undefined, callerMatches may be undefined as well
        if (!callerMatches) return 'caller_not_found'
        //console.log("e ->", e, "stack ->", e.stack, "allMatches ->", allMatches, "callerMatches ->", callerMatches);
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        if (typeof callerMatches[3] === 'undefined' && callerMatches[0].includes('http')) {
          // caller function is a react function component -> GLOBAL
          return 'GLOBAL'
        } else if (callerMatches[3] && callerMatches[2] === 'async') {
          // caller function has an 'async' prefix -> caller name is in the next matching-group
          return callerMatches[3]
        } else {
          return callerMatches[3] // the regex-match group that holds the caller name
        }
      }
      return ''
    }
  }

  getLogPrefix(logTime: Date, scope: string): string {
    const timeString = getTimeString(logTime)
    return timeString + ` || ${this.context} [${scope}]:`
  }

  logToConsole(mode: 'debug' | 'info' | 'warn' | 'error', ...content: any) {
    const callerName = this.getCallerName(3)
    const scope = callerName.match(new RegExp(this.context, 'i')) ? 'GLOBAL' : callerName

    const prefix = this.getLogPrefix(new Date(), scope)

    switch (mode) {
      case 'debug':
        console.debug(prefix, ...content)
        break
      case 'info':
        console.info(prefix, ...content)
        break
      case 'warn':
        console.warn(prefix, ...content)
        break
      case 'error':
        console.error(prefix, ...content)
        break
    }
  }

  /**
   * Print error messages to the console. Use this method for notifying developers that something went wrong or failed.
   * @param content anything
   * @returns
   */
  error(...content: any) {
    if (this.enable && severity['error'] >= severity[this.logLevel]) {
      this.logToConsole('error', ...content)
    }
  }

  /**
   * Print warning messages to the console. Use this method for notifying developers of unexpected behavior of functions or unexpected values of variables, ...
   * @param content anything
   * @returns
   */
  warn(...content: any) {
    if (this.enable && severity['warn'] >= severity[this.logLevel]) {
      this.logToConsole('warn', ...content)
    }
  }

  /**
   * Print info messages to the console. Use this method for and only for describing the high-level program flow.
   * @param content anything
   * @returns
   */
  info(...content: any) {
    if (this.enable && severity['info'] >= severity[this.logLevel]) {
      this.logToConsole('info', ...content)
    }
  }

  /**
   * Print debugging information to the console. Use this method for any debugging purposes that are not covered by the error/warn/info log methods.
   * @param content anything
   * @returns
   */
  debug(...content: any) {
    if (this.enable && severity['debug'] >= severity[this.logLevel]) {
      this.logToConsole('debug', ...content)
    }
  }
}
